import { MinusCircleOutlined } from '@ant-design/icons';
import {
  ConfigProvider,
  Form,
  message,
  Tag,
  Typography,
  Input
} from 'antd';
import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import CardContainer from '../../../components/CardContainer';
import { observer } from 'mobx-react-lite';
import StepperFooter from '../../../components/StepperFooter';
import CategoryKeywordModel from '../models/CategoryKeywordModel';
import AntdCustomCheckbox from '../../../components/buttons/AntdCustomCheckbox';
import { BodyText, SmallText } from '../../../components/ui/Typography';
import ActivityEntity from '../domain/ActivityEntity';
import { validateTagOrKeyword } from '../../../helpers/ValidateTags';
import { AnimatePresence, motion } from 'framer-motion';
import {
  fadeInUpVariants,
  staggerContainerVariants
} from '../../../components/animations/StepAnimations';

const { Title, Text } = Typography;

interface FCProps {
  stepData: {
    activity: ActivityEntity;
    firstAreaWithNoKeywordsProvided: any;
    stepTitle: string;
    subTitle: string;
  };
}

const KeywordsActivityAreasStep: React.FC<FCProps> = observer(({ stepData }) => {
  const { t } = useTranslation();
  const [isStepLoading, setIsStepLoading] = React.useState(false);
  const [customInputValue, setCustomInputValue] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [isVisible, setIsVisible] = React.useState(true);
  const [form] = Form.useForm();

  const area = stepData.firstAreaWithNoKeywordsProvided;
  const areaName = area.displayName;
  const keywords = area.keywords;

  let title: React.ReactNode = (
    <>
      {t('Services and products for area')}{' '}
      <b>"{areaName}"</b>
    </>
  );

  let allAddedKeywords = stepData.activity.getCheckedKeywordsForArea(area.id);
  let disableAllUncheckedKeywords = allAddedKeywords.length === 5;

  const handleAddCustomKeyword = () => {
    const trimmedValue = customInputValue.trim();

    if (!trimmedValue) {
      setErrorMessage('');
      return;
    }

    if (
      allAddedKeywords.some(
        (keyword) => keyword.toLowerCase() === trimmedValue.toLowerCase()
      )
    ) {
      message.warning(t('This service/product already exists'));
      setCustomInputValue('');
      setErrorMessage('');
      return;
    }

    if (
      keywords.some(
        (keyword) => keyword.toLowerCase() === trimmedValue.toLowerCase()
      )
    ) {
      const existingKeyword = keywords.find(
        (keyword) => keyword.toLowerCase() === trimmedValue.toLowerCase()
      );
      if (existingKeyword) {
        stepData.activity.setAreaKeywordChecked(area.id, existingKeyword, true);
        setCustomInputValue('');
        setErrorMessage('');
        return;
      }
    }

    if (!validateTagOrKeyword(trimmedValue)) {
      setErrorMessage(
        t(
          'The value must be between 2 and 100 characters long and can only contain letters (A-Z), numbers (0-9), dashes ( - ), and slashes ( / ). Special symbols are not allowed.'
        )
      );
      return;
    }

    setErrorMessage('');
    stepData.activity.setAreaKeywordChecked(area.id, trimmedValue, true);
    setCustomInputValue('');
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustomKeyword();
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={staggerContainerVariants}
        >
          <CardContainer title={title}>
            <div className="pt-2 mb-7 text-left sm:text-center">
              <motion.div variants={fadeInUpVariants} className="mb-[48px]">
                <BodyText className="mb-2">
                  <Trans
                    i18nKey="keywordsStep.description"
                    defaults="Help us to determine the right <b>services and products</b> which best suit. <br> Define <b>1-5 services or products</b> for this category."
                    components={{ b: <b /> }}
                  />
                </BodyText>
  
              </motion.div>

              <div className="flex flex-col gap-5">
                <motion.div 
                  variants={fadeInUpVariants}
                  className="flex flex-wrap gap-4 justify-start sm:justify-center"
                >
                  {keywords.map((keyword, index) => (
                    <div key={`${keyword}-${index}`}>
                      <AntdCustomCheckbox
                        appearance="basic"
                        disabled={
                          disableAllUncheckedKeywords &&
                          !stepData.activity.isAreaKeywordChecked(area.id, keyword)
                        }
                        checked={stepData.activity.isAreaKeywordChecked(area.id, keyword)}
                        onChange={(e) => {
                          stepData.activity.setAreaKeywordChecked(
                            area.id,
                            keyword,
                            e.target.checked
                          );
                        }}>
                        <Typography.Text>{keyword}</Typography.Text>
                      </AntdCustomCheckbox>
                    </div>
                  ))}
                </motion.div>

                <motion.div 
                  variants={fadeInUpVariants}
                  className="py-2 flex flex-col gap-2 text-left"
                >
                  <Typography.Text className='text-left px-[18px]' strong>
                    {t('Complete your own services and products')}
                  </Typography.Text>
                  <div
                    className="flex flex-wrap gap-2 px-[16px] py-[8px] rounded-full shadow-sm justify-start sm:justify-center min-h-[48px] items-center"
                    style={{
                      backgroundColor:
                        allAddedKeywords.length < 5
                          ? 'rgba(238, 251, 255, 1)'
                          : 'white',
                      border: `1px solid ${
                        allAddedKeywords.length < 5
                          ? 'rgba(183, 235, 255, 1)'
                          : 'rgba(0, 0, 0, 0.15)'
                      }`
                    }}
                    onClick={() => {
                      const input = document.getElementById(
                        'custom-area-keyword-input'
                      ) as HTMLInputElement;
                      if (input) input.focus();
                    }}>
                    {allAddedKeywords
                      .filter((keyword) => !keywords.includes(keyword))
                      .map((keyword, idx) => (
                        <Tag
                          key={`${keyword}-${idx}`}
                          icon={
                            <MinusCircleOutlined
                              onClick={() =>
                                stepData.activity.setAreaKeywordChecked(
                                  area.id,
                                  keyword,
                                  false
                                )
                              }
                            />
                          }
                          color="default"
                          style={{
                            fontSize: 12,
                            padding: '1px 8px',
                            borderRadius: '50px',
                            backgroundColor: '#ffffff',
                            border: '1px solid rgba(0, 0, 0, 0.15)'
                          }}>
                          {keyword}
                        </Tag>
                      ))}

                    <Input
                      id="custom-area-keyword-input"
                      value={customInputValue}
                      onChange={(e) => {
                        setCustomInputValue(e.target.value);
                        setErrorMessage('');
                      }}
                      onKeyPress={handleKeyPress}
                      onBlur={handleAddCustomKeyword}
                      disabled={allAddedKeywords.length >= 5}
                      style={{
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none',
                        background: 'transparent',
                        padding: 0,
                        fontSize: 12,
                        flex: 1,
                        minWidth:
                          allAddedKeywords.filter(
                            (keyword) => !keywords.includes(keyword)
                          ).length === 0
                            ? '200px'
                            : '100px'
                      }}
                      className="custom-keyword-input"
                    />
                  </div>
                  {errorMessage && (
                    <SmallText className='px-[18px]' style={{ color: '#ff4d4f' }}>
                      {errorMessage}
                    </SmallText>
                  )}
                  <SmallText className='px-[16px]'>{t('Press [Enter] to add your own services or products')}</SmallText>
                </motion.div>
              </div>
            </div>
            
            <motion.div variants={fadeInUpVariants}>
              <StepperFooter
                buttonId="category-save-next"
                buttonGaEvent="category-save-next"
                loading={isStepLoading}
                disabled={allAddedKeywords.length === 0}
                onFormsValidationSuccess={async (formData, nextStep, toStep) => {
                  setIsStepLoading(true);

                  let allAddedKeywords = stepData.activity.getCheckedKeywordsForArea(area.id);
                  if (allAddedKeywords.length === 0) {
                    message.error(
                      t(
                        'Please select at least one service and product or add a custom one.'
                      )
                    );
                    setIsStepLoading(false);
                    return;
                  }

                  // validate the form
                  try {
                    await form.validateFields();
                  } catch (error) {
                    console.error('Form validation failed:', error);
                    message.error(
                      t(
                        'Please select at least one service and product or add a custom one.'
                      )
                    );
                    setIsStepLoading(false);
                    return;
                  }

                  try {
                    // First mark the area as having keywords provided
                    stepData.activity.setKeywordsProvidedForArea(area.id, true);
                    
                    // Start the fade out animation
                    setIsVisible(false);
                    
                    // Wait for animation to complete before navigating
                    setTimeout(async () => {
                      await CategoryKeywordModel.moveToNextStep();
                      setIsStepLoading(false);
                    }, 200);
                  } catch (error) {
                    console.error('Error moving to next step:', error);
                    message.error(
                      t(
                        'An error occurred while moving to the next step. Please try again later.'
                      )
                    );
                    setIsVisible(true);
                    setIsStepLoading(false);
                  }
                }}
                count={allAddedKeywords.length}
                maxCount={5}
                counterText={`${allAddedKeywords.length}/5 ${t('selected')}`}
              />
            </motion.div>
          </CardContainer>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default KeywordsActivityAreasStep;
